<script setup lang="ts">
import { Download, Loader2 } from 'lucide-vue-next';
import { ref } from 'vue';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/toast/use-toast';
import { importAllAzureGroups } from '@/services/user_groups';

interface Props {
    tenantId: string;
    disabled?: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
    'import-completed': [];
}>();

const { toast } = useToast();

const importing = ref(false);
const showConfirmDialog = ref(false);

const handleClick = () => {
    showConfirmDialog.value = true;
};

const handleConfirmImport = async () => {
    showConfirmDialog.value = false;
    importing.value = true;

    try {
        const result = await importAllAzureGroups(props.tenantId);

        let description = '';
        if (result.imported > 0 && result.skipped > 0) {
            description = `Imported ${result.imported} group${result.imported === 1 ? '' : 's'}, ${result.skipped} already existed`;
        } else if (result.imported > 0) {
            description = `Successfully imported ${result.imported} group${result.imported === 1 ? '' : 's'}`;
        } else if (result.skipped > 0) {
            description = `All ${result.skipped} group${result.skipped === 1 ? '' : 's'} were already imported`;
        } else {
            description = 'No groups to import';
        }

        toast({
            title: 'Import Complete',
            description,
            duration: 4000
        });

        emit('import-completed');
    } catch (err) {
        console.error('Error importing all groups:', err);

        const errorMessage =
            err instanceof Error ? err.message : 'Failed to import groups';

        toast({
            title: 'Import Failed',
            description: errorMessage,
            variant: 'destructive',
            duration: 5000
        });
    } finally {
        importing.value = false;
    }
};
</script>

<template>
    <div>
        <Button
            @click="handleClick"
            :disabled="disabled || importing"
            size="sm"
            variant="default"
            class="flex items-center gap-2"
        >
            <Loader2 v-if="importing" class="w-4 h-4 animate-spin" />
            <Download v-else class="w-4 h-4" />
            {{ importing ? 'Importing...' : 'Import All' }}
        </Button>

        <!-- Confirmation Dialog -->
        <AlertDialog v-model:open="showConfirmDialog">
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle
                        >Import All Azure AD Groups?</AlertDialogTitle
                    >
                    <AlertDialogDescription>
                        This will import all Azure AD groups that haven't been
                        imported yet. Groups with duplicate names or that
                        already exist will be skipped automatically.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction @click="handleConfirmImport">
                        Import All Groups
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    </div>
</template>
